import { ComponentPropsWithoutRef, forwardRef } from "react";
import cx from "classnames";

import styles from "./Skeleton.module.scss";
import { LayoutUtilProps } from "../../types";
import { useLayoutPropsUtil } from "../../internal/hooks";

/**
 * Props for the SkeletonRectangle component
 * @extends Omit<ComponentPropsWithoutRef<"div">, "children">
 * @extends Omit<LayoutUtilProps, "flex" | "flexBasis" | "flexGrow" | "flexShrink">
 */
export type SkeletonRectangleProps = Omit<
  ComponentPropsWithoutRef<"div">,
  "children"
> &
  Omit<LayoutUtilProps, "flex" | "flexBasis" | "flexGrow" | "flexShrink"> & {
    /**
     * The height of the rectangular skeleton in CSS units.
     * @default 56px
     */
    height?: string;
    /**
     * Enables the AI visual style with blue gradient colors.
     */
    ai?: boolean;
  };

/**
 * SkeletonRectangle component for displaying rectangular loading placeholders.
 *
 * Features:
 * - Rectangular skeleton placeholder with customizable height
 * - Animated pulsing effect for loading indication
 * - Accessible with proper ARIA attributes
 * - Layout utility props for positioning and spacing
 * - Customizable styling through className and style props
 * - Progress bar role for screen readers
 * - Medium border radius for consistent styling
 *
 * @example
 * <Skeleton.Rectangle height="100px" />
 *
 * <Skeleton.Rectangle
 *   height="2rem"
 *   className="custom-skeleton"
 *   aria-label="Loading image"
 * />
 */
export const SkeletonRectangle = forwardRef<
  HTMLDivElement,
  SkeletonRectangleProps
>((props, ref) => {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const {
    className,
    height,
    ai,
    "aria-label": ariaLabel = "Loading content",
    style,
    ...rest
  } = componentProps;

  const SkeletonClassNames = cx(
    [styles["skeleton"]],
    [styles["rectangle"]],
    styles["shade"],
    ai && styles["ai"],
    className,
  );

  const styleCombined = {
    ...style,
    ...layoutStyles,
    "--height": height,
  };

  return (
    <div
      {...rest}
      data-anv="skeleton-rectangle"
      role="progressbar"
      aria-busy="true"
      className={SkeletonClassNames}
      style={styleCombined}
      aria-label={ariaLabel}
      ref={ref}
    />
  );
});

SkeletonRectangle.displayName = "SkeletonRectangle";
